import { DateTime } from 'luxon';
import React, { useState } from 'react';

import { getRouteStackPath } from '@portal/config/routes';
import { TransactionFlow, TransactionType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';

import Logo from '@portal/assets/svg/logos/ic_logo.svg?react';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import { useNavigation } from '@portal/hooks/useNavigation';
import { getTrasactionIcon } from '@portal/utils/logo';

interface IProps {
  isExtract?: boolean;
  isEscrow?: boolean;
  transactionDetails?: boolean;
  transactionIcon?: boolean;
  transactionTime?: boolean;
  transaction?: bank.StatementItem | bank.StatementItemPismo | null;
  directData?: {
    iconUrl?: string;
    title?: string;
    description?: string;
    value?: string;
    date?: {
      date: string;
      formatter?: Intl.DateTimeFormatOptions;
    };
  };
  isScheduled?: boolean;
}

const TransactionItem: React.FC<IProps> = ({
  isExtract,
  isEscrow,
  transactionDetails,
  transactionIcon,
  transactionTime,
  transaction,
  directData,
  isScheduled,
}: IProps) => {
  const navigate = useNavigation();
  const [reconciliationModalVisible, setReconciliationModalVisible] =
    useState(false);

  const hasTransactionInfo = !!transaction;

  const handleExtractDetails = (transaction: bank.Transaction) => {
    if ('isClickable' in transaction && transaction.isClickable === false) {
      setReconciliationModalVisible(true);
      return;
    }

    if (transaction.type === TransactionType.INVESTMENTS) {
      return navigate(
        `${getRouteStackPath('INVESTMENTS', 'INVESTMENT_RECEIPT')}${
          transaction.id
        }`,
        {
          state: {
            isExtract: isExtract,
          },
        }
      );
    }

    navigate(
      `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${transaction.id}`,
      {
        state: {
          isExtract: isExtract,
          isEscrow: isEscrow,
          isScheduled: isScheduled,
        },
      }
    );
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const getTransactionTitle = (transaction) => {
    if (!transaction) return;

    if (transaction.type !== TransactionType.TED && transaction.description) {
      const isInvestmentRedemption =
        translate(
          'PAGES.HOME.TRADING_NOTES.BUTTONS.REDEMPTION'
        ).toUpperCase() === transaction.description.split(' ')[2];

      return isInvestmentRedemption
        ? translate('PAGES.HOME.TRADING_NOTES.INVESTMENT_REDEMPTION')
        : transaction.title;
    }

    if (transaction.isReversed) {
      return `${transaction.title} ${translate(
        'ENUMERATORS.TED_TYPE.CASH_IN_REVERSED.TEXT'
      )}`;
    }

    if (
      [
        TransactionType.CREDIT,
        TransactionType.DEBIT,
        TransactionType.REVERSAL,
      ].includes(transaction.type)
    ) {
      return `${transaction.title} ${getEnumDescription(
        transaction.flow,
        'TED_TYPE_MALE'
      )}`;
    }

    return `${transaction.title} ${getEnumDescription(
      transaction.flow,
      'TED_TYPE'
    )}`;
  };

  const getFlowSymbol = (flow: TransactionFlow | null) => {
    if (!flow) {
      return '';
    }

    if (flow === TransactionFlow.CASH_IN) {
      return '+ ';
    } else {
      return '- ';
    }
  };

  const getFlowClass = () => {
    if (!transaction?.flow) return 'default';

    return transaction.flow === TransactionFlow.CASH_IN
      ? 'positive'
      : 'negative';
  };

  const getAmount = () => {
    if (!transaction) return 0;

    if (
      transaction.type === TransactionType.BILLET &&
      transaction.totalAmount != null
    ) {
      return transaction.totalAmount;
    }

    return transaction.amount ?? 0;
  };

  const hasOnClick =
    !!transaction &&
    !!transaction.flow &&
    !!transaction.id &&
    !!transactionDetails;

  return (
    <>
      <div
        className={
          hasOnClick
            ? 'transaction-item__div'
            : 'transaction-item__unclickable-div'
        }
        {...(hasOnClick && {
          onClick: () => {
            handleExtractDetails(transaction);
          },
        })}
      >
        <div className="transaction-item__content">
          {transactionIcon ? (
            <div className="transaction-item__icon-container">
              {hasTransactionInfo &&
              transaction?.iconUrl &&
              isValidUrl(transaction?.iconUrl) ? (
                <img
                  src={transaction.iconUrl}
                  alt={translate('SHARED.ALT_IMG_PROFILE')}
                  className="transaction-item__icon"
                />
              ) : directData?.iconUrl ? (
                <img
                  src={directData.iconUrl}
                  alt={translate('SHARED.ALT_IMG_PROFILE')}
                  className="transaction-item__icon"
                />
              ) : transaction?.iconUrl &&
                getTrasactionIcon(transaction?.iconUrl) ? (
                <img
                  src={getTrasactionIcon(transaction?.iconUrl)?.toString()}
                  alt={translate('SHARED.ALT_IMG_PROFILE')}
                  className="transaction-item__icon"
                />
              ) : (
                <Logo
                  className="transaction-item__icon"
                  width={40}
                  height={40}
                />
              )}
            </div>
          ) : null}
          <div className="transaction-item__text-container">
            <div className="transaction-item__main-row">
              <p className="transaction-item__title">
                {hasTransactionInfo
                  ? getTransactionTitle(transaction)
                  : directData?.title}
              </p>
              <p
                className={`transaction-item__amount transaction-item__amount--${getFlowClass()}`}
              >
                {hasTransactionInfo
                  ? `${getFlowSymbol(transaction.flow)}${moneyFormat(
                      getAmount()
                    )}`
                  : directData?.value}
              </p>
            </div>
            <div className="transaction-item__secondary-row">
              <p className="transaction-item__description">
                {hasTransactionInfo
                  ? transaction.description
                  : directData?.description}
              </p>
              {hasTransactionInfo && transactionTime && transaction?.flow ? (
                <p className="transaction-item__date">
                  {DateTime.fromISO(
                    transaction.transactionAt ??
                      transaction.transactionScheduleAt
                  ).toLocaleString(DateTime.DATETIME_SHORT)}
                </p>
              ) : (
                directData?.date && (
                  <p className="transaction-item__date">
                    {DateTime.fromISO(directData.date.date).toLocaleString(
                      directData.date.formatter ?? DateTime.DATETIME_SHORT
                    )}
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <GenericModal
        title={translate('PAGES.EXTRACT.CONCILIATION.TITLE')}
        description={translate('PAGES.EXTRACT.CONCILIATION.MESSAGE')}
        onClose={() => setReconciliationModalVisible(false)}
        visible={reconciliationModalVisible}
        actions={[
          {
            label: translate('PAGES.EXTRACT.CONCILIATION.BUTTON'),
            onClick: () => setReconciliationModalVisible(false),
            variant: 'outlined',
          },
        ]}
      />
    </>
  );
};

export default TransactionItem;
